/**
 * Tracking validator — Zod-pattern in vanilla JS.
 *
 * Wraps window.posthog.capture / register with schema validation against
 * canonical enums sourced from <script id="trk-cfg" type="application/json">.
 * Mismatches log to console (dev visibility) but events STILL FIRE — analytics
 * never breaks UX. In prod, console warnings are silent unless DevTools is open.
 *
 * Inline config contract (matches verticals/<slug>/canonical.json):
 *   { vertical, events: [{ name, required, optional }], enums: { cta_tiers, ... } }
 *
 * Usage:
 *   trackedCapture('eligibility checked', { score: 73, ... });
 *   trackedRegister({ last_eligibility_score_band: '70-84', ... });
 */
(function (root) {
  'use strict';

  function warn(msg) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[tracking] ' + msg);
    }
  }

  /* Read inline JSON config from <script id="trk-cfg"> at script-load time. */
  function readInlineConfig() {
    if (typeof document === 'undefined') return null;
    var t = document.getElementById('trk-cfg');
    if (!t) return null;
    try {
      return JSON.parse(t.textContent || '{}');
    } catch (e) {
      warn('trk-cfg parse error: ' + e);
      return null;
    }
  }

  var CFG = readInlineConfig();

  /* ===== Build canonical schemas from CFG (with safe fallbacks) =====
   * If config absent (legacy / SSR / test without injection), validators
   * become permissive — runtime never throws.
   */
  function setOf(arr) {
    var s = new Set();
    if (Array.isArray(arr)) for (var i = 0; i < arr.length; i++) s.add(arr[i]);
    return s;
  }

  var EVENTS = new Set();
  var EVENT_PROPS = {};
  if (CFG && Array.isArray(CFG.events)) {
    for (var i = 0; i < CFG.events.length; i++) {
      var ev = CFG.events[i];
      if (!ev || !ev.name) continue;
      EVENTS.add(ev.name);
      var req = Array.isArray(ev.required) ? ev.required : [];
      var opt = Array.isArray(ev.optional) ? ev.optional : [];
      if (req.length || opt.length) {
        EVENT_PROPS[ev.name] = { required: req, optional: opt };
      }
    }
  }

  var enums = (CFG && CFG.enums) || {};
  var CTA_TIERS = setOf(enums.cta_tiers);
  var SECTION_NAMES = setOf(enums.section_names);
  var RISK_BANDS = setOf(enums.risk_bands);
  var SCORE_BANDS = setOf(enums.score_bands);
  var DBR_BANDS = setOf(enums.dbr_bands);
  var AMOUNT_BANDS = setOf(enums.amount_bands);

  function validateEvent(eventName, props) {
    var issues = [];

    if (EVENTS.size > 0 && !EVENTS.has(eventName)) {
      issues.push('unknown event "' + eventName + '" (not in canonical list)');
    }

    var contract = EVENT_PROPS[eventName];
    if (contract) {
      var have = props ? Object.keys(props) : [];
      for (var r = 0; r < contract.required.length; r++) {
        if (have.indexOf(contract.required[r]) === -1) {
          issues.push('missing required prop "' + contract.required[r] + '" for "' + eventName + '"');
        }
      }
      var allowed = new Set(contract.required.concat(contract.optional));
      for (var k = 0; k < have.length; k++) {
        if (!allowed.has(have[k])) {
          issues.push('unexpected prop "' + have[k] + '" for "' + eventName + '" (not in contract)');
        }
      }
    }

    /* Per-property value enum checks */
    if (props) {
      if (props.risk_band !== undefined && RISK_BANDS.size && !RISK_BANDS.has(props.risk_band)) {
        issues.push('risk_band="' + props.risk_band + '" not in {high, low}');
      }
      if (props.score_band !== undefined && SCORE_BANDS.size && !SCORE_BANDS.has(props.score_band)) {
        issues.push('score_band="' + props.score_band + '" not in canonical bands');
      }
      if (props.dbr_band !== undefined && DBR_BANDS.size && !DBR_BANDS.has(props.dbr_band)) {
        issues.push('dbr_band="' + props.dbr_band + '" not in canonical bands');
      }
      if (props.amount_band !== undefined && AMOUNT_BANDS.size && !AMOUNT_BANDS.has(props.amount_band)) {
        issues.push('amount_band="' + props.amount_band + '" not in canonical bands');
      }
      if (props.section_name !== undefined && SECTION_NAMES.size && !SECTION_NAMES.has(props.section_name)) {
        issues.push('section_name="' + props.section_name + '" not in canonical 8-tier (or extensions)');
      }
      if (typeof props.score === 'number' && (props.score < 0 || props.score > 100)) {
        issues.push('score=' + props.score + ' out of range 0-100');
      }
    }

    return issues;
  }

  /* Wrapped capture — validates then fires. Never throws. */
  function trackedCapture(eventName, props) {
    try {
      var issues = validateEvent(eventName, props);
      for (var i = 0; i < issues.length; i++) warn(issues[i]);
    } catch (err) { /* swallow validator errors */ }

    if (root.posthog && typeof root.posthog.capture === 'function') {
      try { root.posthog.capture(eventName, props || {}); }
      catch (err) { /* analytics never breaks UX */ }
    }
  }

  /* Wrapped register — sets super-properties */
  function trackedRegister(props) {
    if (root.posthog && typeof root.posthog.register === 'function') {
      try { root.posthog.register(props || {}); }
      catch (err) { /* analytics never breaks UX */ }
    }
  }

  /* Expose to global scope (classic script load context) */
  root.trackedCapture = trackedCapture;
  root.trackedRegister = trackedRegister;
  root.__trackingCanonical = {
    /* Exposed for tests and the build-time cross-check */
    events: Array.from(EVENTS),
    cta_tiers: Array.from(CTA_TIERS),
    section_names: Array.from(SECTION_NAMES),
    risk_bands: Array.from(RISK_BANDS),
    score_bands: Array.from(SCORE_BANDS),
    dbr_bands: Array.from(DBR_BANDS),
    amount_bands: Array.from(AMOUNT_BANDS)
  };

  /* ===== Global click delegate
   * Reads underscore-style data-event attribute, normalizes to space-separated
   * canonical event name, then calls trackedCapture.
   *
   * 2026-05-04 — previously HTML had data-event attrs across 17+ WhatsApp links
   * plus countless CTAs, but no JS binder existed. PostHog never received the
   * named events for real users on link click. GTM Tag 8 fired Google Ads
   * conversion (awct) so Google Ads got attribution, but PostHog whatsapp /
   * cta / call event funnels showed 0% real users.
   *
   * Capture-phase listener fires BEFORE browser navigates to external link, so
   * trackedCapture runs in time. PostHog SDK uses sendBeacon for unload-resistant
   * delivery — no transport config needed.
   * See refs/debug-log.md 2026-05-04 entry for full incident.
   * ===== */
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('click', function (e) {
      var el = e.target && e.target.closest && e.target.closest('[data-track][data-event]');
      if (!el) return;
      var rawEv = el.getAttribute('data-event') || '';
      if (!rawEv) return;
      /* Normalize: data-event="whatsapp_clicked" → 'whatsapp clicked' (canonical) */
      var ev = rawEv.replace(/_/g, ' ');
      var props = {
        cta_id: el.getAttribute('data-cta-id') || '',
        cta_text: (el.getAttribute('data-cta-text') || (el.textContent || '').trim().slice(0, 80)),
        location: el.getAttribute('data-location') || '',
        channel: el.getAttribute('data-channel') || '',
        funnel: el.getAttribute('data-funnel') || '',
        cta_tier: el.getAttribute('data-cta-tier') || '',
        form: el.getAttribute('data-form') || ''
      };
      /* Strip empty props for cleaner PostHog UI */
      Object.keys(props).forEach(function (k) { if (!props[k]) delete props[k]; });
      trackedCapture(ev, props);
    }, true);  /* capture: true — fires before bubble + before navigation */
  }
})(typeof window !== 'undefined' ? window : globalThis);
